import {
  observationTypeStyles,
  severityStyles,
} from 'pages/pointClouds/components/dropdowns/EnumStyles'
import {
  ObservationTypeTag,
  SeverityTag,
  TicketStatusTag,
} from 'pages/pointClouds/components/shared/ObservationTags'
import React from 'react'
import styled from 'styled-components'
import { GeckoObservationTicket } from 'types'

const CardContainer = styled.div`
  background-color: rgba(40, 40, 40, 0.8);
  border-radius: 5px;
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background-color: rgba(60, 60, 60, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
  }
`

const Header = styled.div`
  height: 40px;
  padding: 5px 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const HeaderLeft = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 48px;
`

const HeaderRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const IconWrapper = styled.div<{ color?: string }>`
  color: ${(props) => props.color || 'inherit'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`

const Content = styled.div`
  flex: 1;
  padding: 12px;
  display: flex;
  align-items: flex-start;
`

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
`

const LabelValueRow = styled(Row)`
  display: grid;
  grid-template-columns: 65px 1fr;
`

const GridRow = styled(Row)`
  display: grid;
  grid-template-columns: 65px 1fr auto 1fr;
  gap: 8px;
  min-width: 0;
`

const Label = styled.span`
  color: #888;
  font-size: 12px;
  white-space: nowrap;
`

const Dot = styled.span`
  margin: 0px;
  color: #888;
  &::before {
    content: '•';
  }
`

const Value = styled.span`
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

const HeaderValue = styled(Value)`
  font-size: 20px;
  font-weight: bold;
`

const RightContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`
interface TicketCardProps {
  ticket: GeckoObservationTicket
}

export const TicketCard: React.FC<TicketCardProps> = ({ ticket }) => {
  const severity = ticket?.data.severity
  const observationType = ticket?.type as unknown as
    | keyof typeof observationTypeStyles
    | undefined

  return (
    <CardContainer>
      <Header>
        <HeaderLeft>
          {severity && severityStyles[severity] && (
            <>
              <IconWrapper color={severityStyles[severity].color}>
                {severityStyles[severity].icon}
              </IconWrapper>
              <SeverityTag severity={severity} />
            </>
          )}
          {observationType && observationTypeStyles[observationType] && (
            <>
              <IconWrapper>
                {observationTypeStyles[observationType].icon}
              </IconWrapper>
              <ObservationTypeTag type={observationType} />
            </>
          )}
        </HeaderLeft>
        <HeaderRight>
          <TicketStatusTag status={ticket.data?.status} />
        </HeaderRight>
      </Header>

      <Content>
        <RightContent>
          <Row>
            <HeaderValue>{ticket.data?.equipment || 'N/A'}</HeaderValue>
          </Row>

          <Row>
            <Value>{ticket.data?.area_id || 'N/A'}</Value>
            <Dot />
            <Value>{ticket.data?.component || 'N/A'}</Value>
          </Row>

          <LabelValueRow>
            <Label>Fault</Label>
            <Value>{ticket.data?.name || 'N/A'}</Value>
          </LabelValueRow>

          <LabelValueRow>
            <Label>Notes</Label>
            <Value>{ticket.data?.description || 'No description'}</Value>
          </LabelValueRow>

          {(ticket.data?.date_found || ticket.data?.original_created_date) && (
            <GridRow>
              <Label>Date</Label>
              <Value>
                {ticket.data?.date_found ||
                  ticket.data?.original_created_date ||
                  'N/A'}
              </Value>
            </GridRow>
          )}

          <GridRow>
            <Label>Notif #</Label>
            <Value>{ticket.data?.notification_number || 'N/A'}</Value>
            <Label>WO #</Label>
            <Value>{ticket.data?.wo_number || 'N/A'}</Value>
          </GridRow>
        </RightContent>
      </Content>
    </CardContainer>
  )
}
